/**
 * Remnant/Offcut Management — Track leftover sheet pieces for reuse
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Recycle, Plus, Trash2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

export default function RemnantManager({ calculations = {} }) {
  const [remnants, setRemnants] = useState([]);
  const [newRemnant, setNewRemnant] = useState({ material: '18mm Plywood', width: '', height: '', source_project: '' });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load saved remnants
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/estimates`, { headers });
        // Use a local storage approach for remnants (simple for now)
        const saved = JSON.parse(localStorage.getItem('bellacasa_remnants') || '[]');
        setRemnants(saved);
      } catch {}
    };
    load();
  }, []);

  const saveRemnants = (updated) => {
    setRemnants(updated);
    localStorage.setItem('bellacasa_remnants', JSON.stringify(updated));
  };

  const addRemnant = () => {
    if (!newRemnant.width || !newRemnant.height) { toast.error('Enter width and height'); return; }
    const r = {
      id: Date.now().toString(),
      material: newRemnant.material,
      width: parseFloat(newRemnant.width),
      height: parseFloat(newRemnant.height),
      area: parseFloat(newRemnant.width) * parseFloat(newRemnant.height),
      source_project: newRemnant.source_project || 'Manual entry',
      added_at: new Date().toISOString(),
      status: 'available',
    };
    saveRemnants([...remnants, r]);
    setNewRemnant({ material: '18mm Plywood', width: '', height: '', source_project: '' });
    toast.success('Remnant added to inventory');
  };

  const removeRemnant = (id) => {
    saveRemnants(remnants.filter(r => r.id !== id));
    toast.success('Remnant removed');
  };

  const markUsed = (id) => {
    saveRemnants(remnants.map(r => r.id === id ? { ...r, status: 'used', used_at: new Date().toISOString() } : r));
    toast.success('Marked as used');
  };

  // Auto-detect remnants from current cutting layout
  const autoDetectRemnants = () => {
    const sheetW = 96; // inches
    const sheetH = 48;
    const sheetArea = sheetW * sheetH;
    const summary = calculations.materialSummary || [];
    const detected = [];

    summary.forEach(mat => {
      if (mat.wastage > 5) {
        const wasteArea = (mat.sheets * 32) - mat.used; // sqft
        if (wasteArea > 2) { // Only track remnants > 2 sqft
          detected.push({
            id: Date.now().toString() + Math.random(),
            material: mat.name,
            width: Math.round(Math.sqrt(wasteArea * 144) * 0.7), // approximate inches
            height: Math.round(Math.sqrt(wasteArea * 144) * 1.3),
            area: wasteArea,
            source_project: 'Current estimate',
            added_at: new Date().toISOString(),
            status: 'available',
          });
        }
      }
    });

    if (detected.length > 0) {
      saveRemnants([...remnants, ...detected]);
      toast.success(`${detected.length} remnant(s) detected and saved`);
    } else {
      toast.info('No significant remnants detected');
    }
  };

  // Suggest reuse
  const availableRemnants = remnants.filter(r => r.status === 'available');
  const totalValue = availableRemnants.reduce((s, r) => s + (r.area || 0) * 50, 0); // rough Rs 50/sqft

  return (
    <div className="space-y-5" data-testid="remnant-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Recycle size={18} className="text-green-600" /><h3 className="font-bold text-stone-800">Remnant / Offcut Management</h3></div>
        <Button size="sm" variant="outline" onClick={autoDetectRemnants} data-testid="auto-detect-remnants"><ArrowRight size={14} className="mr-1" />Auto-Detect from Layout</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-green-50 text-green-800">
          <p className="text-[10px] uppercase font-bold opacity-70">Available Remnants</p>
          <p className="text-xl font-extrabold">{availableRemnants.length}</p>
        </div>
        <div className="p-3 rounded-lg bg-blue-50 text-blue-800">
          <p className="text-[10px] uppercase font-bold opacity-70">Total Area</p>
          <p className="text-xl font-extrabold">{availableRemnants.reduce((s, r) => s + (r.area || 0), 0).toFixed(1)} sqft</p>
        </div>
        <div className="p-3 rounded-lg bg-amber-50 text-amber-800">
          <p className="text-[10px] uppercase font-bold opacity-70">Est. Value</p>
          <p className="text-xl font-extrabold">Rs {totalValue.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Add new remnant */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <h4 className="font-semibold text-sm text-stone-700 mb-3">Add Remnant Manually</h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
            <div>
              <Label>Material</Label>
              <select className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" value={newRemnant.material} onChange={e => setNewRemnant({ ...newRemnant, material: e.target.value })}>
                {['18mm Plywood', '12mm Plywood', '8mm Plywood', '16mm HDHMR', 'Internal Laminate', 'External Laminate'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div><Label>Width (in)</Label><Input type="number" value={newRemnant.width} onChange={e => setNewRemnant({ ...newRemnant, width: e.target.value })} /></div>
            <div><Label>Height (in)</Label><Input type="number" value={newRemnant.height} onChange={e => setNewRemnant({ ...newRemnant, height: e.target.value })} /></div>
            <div><Label>Source</Label><Input value={newRemnant.source_project} onChange={e => setNewRemnant({ ...newRemnant, source_project: e.target.value })} placeholder="Project name" /></div>
            <Button size="sm" onClick={addRemnant} data-testid="add-remnant-btn"><Plus size={14} className="mr-1" />Add</Button>
          </div>
        </CardContent>
      </Card>

      {/* Inventory table */}
      {remnants.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-2 px-2">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="remnant-table">
                <thead><tr className="bg-stone-50 border-b">
                  {['Material', 'Size (in)', 'Area', 'Source', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left font-semibold">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {remnants.map(r => (
                    <tr key={r.id} className={`border-b ${r.status === 'used' ? 'opacity-50' : ''}`}>
                      <td className="px-2 py-1.5">{r.material}</td>
                      <td className="px-2 py-1.5 font-mono">{r.width} x {r.height}</td>
                      <td className="px-2 py-1.5">{(r.area || 0).toFixed(1)} sqft</td>
                      <td className="px-2 py-1.5 text-stone-500">{r.source_project}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-1.5 flex gap-1">
                        {r.status === 'available' && <button onClick={() => markUsed(r.id)} className="text-blue-600 hover:underline text-[10px]">Use</button>}
                        <button onClick={() => removeRemnant(r.id)} className="text-red-500 hover:underline text-[10px]">Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
